// SPDX-License-Identifier: GPL-3.0-or-later
// The ball trail. Each ball has a ring of its last positions. The colour of a sample follows the
// speed of the ball at that time (palette.ts, speed bands). The older a sample, the smaller and
// the darker it is. All arrays are preallocated: `update` allocates nothing.

import type { BallFrame } from "../../shared/contracts.ts";
import { strengthFromSpeed } from "../../shared/feedback/index.ts";
import { colorOf, speedHex } from "./palette.ts";
import { createPointCloud, type PointCloud } from "./points.ts";

/** The most balls that have a trail. */
export const MAX_TRAIL_BALLS = 8;
/** Samples in the trail of one ball. */
export const TRAIL_LENGTH = 20;

const TRAIL_CAP = MAX_TRAIL_BALLS * TRAIL_LENGTH;

/** The trails of all balls. */
export interface Trails {
  readonly cloud: PointCloud;
  /** Add the balls of a frame. `dtMs` is the time since the last call. `headSize` is the size of the newest sample. */
  update(balls: readonly BallFrame[], dtMs: number, headSize: number, sizeScale: number): void;
  /** Forget every trail. */
  clear(): void;
}

interface TrailState {
  readonly cloud: PointCloud;
  readonly ids: Int32Array;
  readonly heads: Int32Array;
  readonly filled: Int32Array;
  readonly seen: Uint8Array;
  readonly last: Float32Array;
  readonly baseColor: Float32Array;
}

/** The slot of a ball. A new ball takes the first free slot. Returns -1 when none is free. */
function trailsSlotOf(st: TrailState, id: number): number {
  let free = -1;
  for (let s = 0; s < MAX_TRAIL_BALLS; s++) {
    if (st.ids[s] === id) return s;
    if (free < 0 && st.ids[s] === -1) free = s;
  }
  if (free >= 0) {
    st.ids[free] = id;
    st.heads[free] = 0;
    st.filled[free] = 0;
  }
  return free;
}

/** Write one sample at the head of the ring of a slot. */
function trailsPush(st: TrailState, s: number, x: number, y: number, z: number, hex: number): void {
  const had = st.filled[s] as number;
  const head = ((st.heads[s] as number) + 1) % TRAIL_LENGTH;
  st.heads[s] = head;
  st.filled[s] = Math.min(TRAIL_LENGTH, had + 1);
  const i = s * TRAIL_LENGTH + head;
  const color = colorOf(hex);
  st.cloud.position[i * 3] = x;
  st.cloud.position[i * 3 + 1] = y;
  st.cloud.position[i * 3 + 2] = z;
  st.baseColor[i * 3] = color.r;
  st.baseColor[i * 3 + 1] = color.g;
  st.baseColor[i * 3 + 2] = color.b;
}

/** The speed of a ball since its last sample. A slot with no sample yet has speed 0. */
function trailsSpeed(
  st: TrailState,
  s: number,
  x: number,
  y: number,
  z: number,
  dtMs: number,
): number {
  if ((st.filled[s] as number) === 0) return 0;
  const dx = x - (st.last[s * 3] as number);
  const dy = y - (st.last[s * 3 + 1] as number);
  const dz = z - (st.last[s * 3 + 2] as number);
  return Math.hypot(dx, dy, dz) / Math.max(dtMs, 1);
}

/** Record one ball position in its slot. */
function trailsTrack(st: TrailState, s: number, ball: BallFrame, dtMs: number): void {
  // The table frame is x, y (down the table), z (up). The scene frame swaps y and z.
  const x = ball.pos.x;
  const y = ball.pos.z;
  const z = ball.pos.y;
  const speed = trailsSpeed(st, s, x, y, z, dtMs);
  st.last[s * 3] = x;
  st.last[s * 3 + 1] = y;
  st.last[s * 3 + 2] = z;
  trailsPush(st, s, x, y, z, speedHex(strengthFromSpeed(speed)));
}

/** Index in the point cloud of the sample `k` steps back from the head. */
function trailsRingIndex(head: number, k: number): number {
  return (((head - k) % TRAIL_LENGTH) + TRAIL_LENGTH) % TRAIL_LENGTH;
}

/** Size and fade one sample. Samples past the filled count are hidden. */
function trailsShadeSample(st: TrailState, s: number, k: number, n: number, size: number): void {
  const i = s * TRAIL_LENGTH + trailsRingIndex(st.heads[s] as number, k);
  const fade = k < n ? (1 - k / TRAIL_LENGTH) ** 2 : 0;
  st.cloud.size[i] = k < n ? size * fade : 0;
  for (let c = 0; c < 3; c++) {
    st.cloud.color[i * 3 + c] = (st.baseColor[i * 3 + c] as number) * fade;
  }
}

/** Size and fade every sample of a slot. */
function trailsShade(st: TrailState, s: number, radius: number, scale: number): void {
  const n = st.filled[s] as number;
  for (let k = 0; k < TRAIL_LENGTH; k++) {
    trailsShadeSample(st, s, k, n, radius * scale);
  }
}

/** Free a slot and hide its samples. */
function trailsRelease(st: TrailState, s: number): void {
  st.ids[s] = -1;
  st.filled[s] = 0;
  for (let k = 0; k < TRAIL_LENGTH; k++) st.cloud.size[s * TRAIL_LENGTH + k] = 0;
}

/** Add one ball of a frame. A ball with no slot is skipped. */
function trailsAddBall(st: TrailState, ball: BallFrame, dtMs: number): void {
  const s = trailsSlotOf(st, ball.id);
  if (s < 0) return;
  st.seen[s] = 1;
  trailsTrack(st, s, ball, dtMs);
}

function trailsUpdate(
  st: TrailState,
  balls: readonly BallFrame[],
  dtMs: number,
  headSize: number,
  sizeScale: number,
): void {
  st.seen.fill(0);
  const count = Math.min(balls.length, MAX_TRAIL_BALLS);
  for (let n = 0; n < count; n++) trailsAddBall(st, balls[n] as BallFrame, dtMs);
  for (let s = 0; s < MAX_TRAIL_BALLS; s++) {
    if (st.seen[s] === 1) trailsShade(st, s, headSize, sizeScale);
    else if (st.ids[s] !== -1) trailsRelease(st, s);
  }
  st.cloud.commit(TRAIL_CAP);
}

function trailsClear(st: TrailState): void {
  for (let s = 0; s < MAX_TRAIL_BALLS; s++) trailsRelease(st, s);
  st.cloud.commit(TRAIL_CAP);
}

/** Make the trails. */
export function createTrails(): Trails {
  const st: TrailState = {
    cloud: createPointCloud(TRAIL_CAP),
    ids: new Int32Array(MAX_TRAIL_BALLS).fill(-1),
    heads: new Int32Array(MAX_TRAIL_BALLS),
    filled: new Int32Array(MAX_TRAIL_BALLS),
    seen: new Uint8Array(MAX_TRAIL_BALLS),
    last: new Float32Array(MAX_TRAIL_BALLS * 3),
    baseColor: new Float32Array(TRAIL_CAP * 3),
  };

  return {
    cloud: st.cloud,
    update(balls, dtMs, headSize, sizeScale) {
      trailsUpdate(st, balls, dtMs, headSize, sizeScale);
    },
    clear() {
      trailsClear(st);
    },
  };
}
